<template>
  <c-flex flex="1" class="additional-service" :flex-direction="{ base: 'column', md: 'row' }">
    <c-flex flex-direction="column" :py="{ base: 'xs', md: 'lg' }">
      <c-heading as="h3" mt="0" size="lg" :mb="{ base: 'sm', md: '2rem' }" font-weight="normal">
        {{ additionalService.label }}
      </c-heading>
      <c-flex @click="onDisabledInputSelection(additionalService.products)">
        <c-radio-group v-model="selectedProductId" @change="handleRadioSelect">
          <c-radio
            v-for="product in additionalService.products"
            :data-cy="`service-product-radio_${product.id}`"
            :key="product.id"
            :value="product.id"
            :is-disabled="!isOrderingAllowed(product)"
            :mb="{ base: 'xs', md: 'sm' }"
            align-items="baseline"
          >
            <!-- No selection -->
            <c-text v-if="isNoSelectionProduct(product.id)" as="span" fontWeight="bold" flex="1">
              {{ $t(`ordering.serviceProducts.${product.id}`) }}
            </c-text>
            <!-- With product name and subtitle -->
            <c-text
              v-else-if="product.name && product.subtitle"
              as="span"
              fontWeight="bold"
              flex="1"
            >
              {{ product.name }}, {{ product.subtitle }}
              <c-text as="span" fontWeight="light"> </c-text>
            </c-text>
            <!-- With product name -->
            <c-text v-else as="span" fontWeight="bold" flex="1">
              {{ product.name }}
              <c-text as="span" fontWeight="light"> </c-text>
            </c-text>

            <c-text fontWeight="normal" v-if="product.price_factor">
              <i18n path="ordering.extendedWarranties.price" tag="span">
                <template #percentage>
                  <c-text fontWeight="bold" as="span">
                    {{ round(product.price_factor * 100, 2) + "%" }}
                  </c-text>
                </template>
              </i18n>
            </c-text>
          </c-radio>
        </c-radio-group>
      </c-flex>
      <c-flex
        v-if="showReadMore"
        class="additional-service_description-read-more"
        align-items="flex-start"
        mt="auto"
      >
        <c-icon name="info" size="1.5rem" color="black" />
        <c-text>
          {{ $t(String(additionalService.additionalInfo?.prefixText)) }}
          <router-link :to="additionalService.additionalInfo?.link" target="_blank">
            <c-text as="span" text-decoration="underline" font-weight="bold">
              {{ $t(String(additionalService.additionalInfo?.linkText)) }}
            </c-text>
          </router-link>
        </c-text>
      </c-flex>
    </c-flex>

    <!-- Addition service description block -->
    <c-flex
      flex-direction="column"
      line-height="1.8"
      :p="{ base: 'md', md: '3xl' }"
      background-color="brand.blueLightest"
      background-repeat="no-repeat"
      background-position="center"
      background-size="cover"
      :background-image="
        additionalService.image &&
        getProductById(selectedProductId).orderingAllowed &&
        isNoSelectionProduct(selectedProductId) &&
        `url(${additionalService.image})`
      "
      white-space="pre-line"
      border-radius="sharper"
      width="100%"
      :max-width="{ base: '100%', md: '50%' }"
      :min-height="{ base: '21rem', xl: '23rem' }"
      position="relative"
      overflow="hidden"
      ml="auto"
    >
      <!-- Show ordering not allowed text if products cannot be ordered -->
      <c-box v-if="!isNoSelectionProduct(selectedProductId)">
        <c-text as="h3" :font-size="{ base: 'md', md: 'xl' }" fontWeight="bold">
          {{ productName(selectedProductId) }}
        </c-text>
        <c-text as="p" v-if="!getProductById(selectedProductId).orderingAllowed" :mt="{ md: 'md' }">
          <c-text as="span" fontWeight="bold">{{
            $t("ordering.extendedWarranties.orderingNotAllowed.title")
          }}</c-text>
          <br />
          {{ $t("ordering.extendedWarranties.orderingNotAllowed.body") }}
        </c-text>
        <!-- Show product description -->
        <c-text as="p" v-else-if="productDescription(selectedProductId)" :mt="{ md: 'md' }">
          {{ productDescription(selectedProductId) }}
        </c-text>
      </c-box>
    </c-flex>
  </c-flex>
</template>

<script lang="ts">
import {
  CBox,
  CFlex,
  CHeading,
  CIcon,
  CList,
  CListItem,
  CRadio,
  CRadioGroup,
  CStack,
  CText,
} from "@chakra-ui/vue";
import { IAdditionalService, IServiceproduct } from "@iloq/shared";
import { defineComponent, PropType } from "vue";
import { serviceproductToOrderRow } from "../../util/ProductUtil";

export default defineComponent({
  props: {
    additionalService: {
      type: Object as PropType<IAdditionalService>,
      required: true,
    },
    isProductInShoppingCart: {
      type: Function as PropType<(product: IServiceproduct) => boolean>,
      required: true,
    },
    onDisabledInputSelection: {
      type: Function as PropType<(products: IServiceproduct[]) => void>,
      required: true,
    },
    round: {
      type: Function as PropType<(number: number, precision: number) => number>,
      required: true,
    },
  },
  components: {
    CHeading,
    CFlex,
    CText,
    CStack,
    CList,
    CListItem,
    CRadio,
    CRadioGroup,
    CIcon,
    CBox,
  },
  data() {
    return {
      selectedProductId: (
        this.additionalService.products.find(this.isProductInShoppingCart) ||
        this.additionalService.products[0]
      ).id,
    };
  },
  methods: {
    isNoSelectionProduct(id: string): boolean {
      const firstProduct = this.additionalService.products[0];
      const priceZero = firstProduct.price_factor === 0;

      return id === firstProduct.id && priceZero;
    },
    getProductById(id: string): IServiceproduct | Record<string, never> {
      return this.additionalService.products.find((product) => product.id === id) || {};
    },
    isOrderingAllowed(product: IServiceproduct): boolean {
      if (!product.orderingAllowed) {
        if (this.isProductInShoppingCart(product)) {
          this.$store.dispatch("shoppingCart/removeServiceproduct", product);
          (this as any).selectedProductId = this.additionalService.products[0].id;
        }
        return false;
      }

      return true;
    },
    handleRadioSelect(productId: string): void | Promise<unknown> {
      const product = this.additionalService.products.find((p) => productId === p.id)!;
      const { currencyCode } = this.$store.state.shoppingCart.totalSum;

      if (this.isNoSelectionProduct(productId)) {
        return this.$store.dispatch("shoppingCart/removeServiceproduct", product);
      }

      const row = serviceproductToOrderRow(product, currencyCode);
      this.$store.dispatch("shoppingCart/addOrReplaceServiceproduct", row);
    },
    // Get active product name with optional subtitle
    productName(id: string) {
      const product =
        this.additionalService.products.find((product: any) => product.id === id) ||
        ({} as IServiceproduct);

      if (product.subtitle) {
        return `${product.name}, ${product.subtitle}`;
      }
      return product.name;
    },
    // Get active product description / feature summary
    productDescription(id: string) {
      return (
        (this.additionalService.products.find((product: any) => product.id === id) || {})
          .feature_summary || ""
      );
    },
  },
  computed: {
    showReadMore(): boolean {
      const { prefixText, linkText, link } = this.additionalService?.additionalInfo || {};
      return !!(prefixText && linkText && link);
    },
  },
});
</script>
